import * as React from 'react';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';

import { Action, Dispatch } from 'redux';
import { AppState } from '..';
import { CommentState } from './comments.reducer';
// import { CommentViewModel } from '@services/CommentService';
import { commentService } from '@services/CommentService';
import { CommentViewModel, CommentType } from '@models/Comment';
import { usersService } from '@services/UsersService';
import { PageViewModel, OptionsViewModel } from '@models/common.models';
import { ProfileViewModel } from '@models/Auth';
import { actions } from 'react-redux-form';
import FormNames from '@constants/FormNames.enum';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum CommentActionType {
  DeleteCommentStart = 'DeleteCommentStart',
  DeleteCommentSuccess = 'DeleteCommentSuccess',
  DeleteCommentFailure = 'DeleteCommentFailure',
  GetCommentsStart = 'GetCommentsStart',
  GetCommentsSuccess = 'GetCommentsSuccess',
  GetCommentsFailure = 'GetCommentsFailure',
  SaveCommentStart = 'SaveCommentStart',
  SaveCommentSuccess = 'SaveCommentSuccess',
  SaveCommentFailure = 'SaveCommentFailure',
  AddUserToComment = 'AddUserToComment',
  RemoveUserFromComment = 'RemoveUserFromComment',
  ClearUsers = 'ClearUsers'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.

/**** Delete ********/
export interface DeleteCommentStartAction extends Action {
  readonly type: CommentActionType.DeleteCommentStart;
  readonly message: string;
}

export interface DeleteCommentSuccessAction extends Action {
  readonly type: CommentActionType.DeleteCommentSuccess;
  readonly commentId: number;
  readonly message: string;
}

export interface DeleteCommentFailureAction extends Action {
  readonly type: CommentActionType.DeleteCommentFailure;
  readonly errorMessage: string;
}

/********** Search **********/
export interface GetCommentsStartAction extends Action {
  readonly query: string | null;
  readonly type: CommentActionType.GetCommentsStart;
}

export interface GetCommentsSuccessAction extends Action {
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly orderBy: string;
  readonly result: PageViewModel<CommentViewModel>;
  readonly type: CommentActionType.GetCommentsSuccess;
}

export interface GetCommentsFailureAction extends Action {
  readonly errorMessage: string;
  readonly type: CommentActionType.GetCommentsFailure;
}

/********* Save ***********/
export interface SaveCommentStartAction extends Action {
  readonly type: CommentActionType.SaveCommentStart;
  readonly message: string;
}

export interface SaveCommentSuccessAction extends Action {
  readonly type: CommentActionType.SaveCommentSuccess;
  readonly message: string;
  readonly commentId: number;
}

export interface SaveCommentFailureAction extends Action {
  readonly type: CommentActionType.SaveCommentFailure;
  readonly errorMessage: string;
}

export interface AddUserToCommentAction extends Action {
  readonly type: CommentActionType.AddUserToComment;
  readonly user: ProfileViewModel;
}

export interface RemoveUserFromCommentAction extends Action {
  readonly type: CommentActionType.RemoveUserFromComment;
  readonly userEmail: string;
}

export interface ClearUsersAction extends Action {
  readonly type: CommentActionType.ClearUsers;
}

/****** Delete *************/
export function deleteComment(commentId: number, commentsType: CommentType) {
  return async (dispatch: Dispatch<AppState>) => {
    try {
      dispatch(deleteCommentStart());
      await commentService.deleteComment(commentId, commentsType);
      dispatch(deleteCommentSuccess(commentId));
    } catch (e) {
      dispatch(deleteCommentFailure(e.message));
    }
  };
}

export function deleteCommentStart(): DeleteCommentStartAction {
  return {
    type: CommentActionType.DeleteCommentStart,
    message: 'Deleting Comment Start'
  };
}

export function deleteCommentSuccess(commentId: number): DeleteCommentSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={`Comment Deleted Successfully`} />,
    ToastOptions.Success
  );

  return {
    type: CommentActionType.DeleteCommentSuccess,
    message: 'Comment Deleted Successfully',
    commentId: commentId
  };
}

export function deleteCommentFailure(errorMessage: string): DeleteCommentFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={errorMessage} />, ToastOptions.Error);

  return {
    type: CommentActionType.DeleteCommentFailure,
    errorMessage: errorMessage
  };
}

/********** Search **********/
export const getComments = (
  entityId: number,
  commentsType: CommentType,
  query: string | null,
  pageSize: number,
  pageNumber: number,
  orderBy: string,
  orderDirection: string
) => {
  return async (dispatch: Dispatch<CommentState>) => {
    dispatch(getCommentsStart(query));
    let result: PageViewModel<CommentViewModel>;
    try {
      result = await commentService.searchComments(
        entityId,
        commentsType,
        query,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection
      );

      dispatch(getCommentsSuccess(pageNumber, pageSize, orderBy, result));
    } catch (e) {
      dispatch(getCommentsFailure(e.toString()));
    }
  };
};

export function getCommentsStart(query: string | null): GetCommentsStartAction {
  return {
    query: query,
    type: CommentActionType.GetCommentsStart
  };
}

export function getCommentsSuccess(
  pageNumber: number,
  pageSize: number,
  orderBy: string,
  result: PageViewModel<CommentViewModel>
): GetCommentsSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    orderBy: orderBy,
    result: result,
    type: CommentActionType.GetCommentsSuccess
  };
}

export function getCommentsFailure(errorMessage: string): GetCommentsFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to search comments. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: CommentActionType.GetCommentsFailure
  };
}

export function searchUsers(value: string, id?: number) {
  return usersService.search(1, 50, value);
}

/********** Get **********/

export function getCommentFromService(id: number) {
  return commentService.getComment(id);
}

/************* Save ***********/
export function saveComment(
  entityId: number,
  commentType: CommentType,
  model: CommentViewModel,
  usersToAction: OptionsViewModel[],
  saveCallback?: Function,
  isCancel?: boolean
) {
  return async (dispatch: Dispatch<AppState>) => {
    try {
      dispatch(saveCommentStart());
      let newCommentId: number;
      // Make sure to delete commentType off of the comment when saving as it messes up automapper
      if (model.commentType) {
        delete model.commentType;
      }

      const saveDto: CommentViewModel = { ...model, employees: usersToAction };
      newCommentId = await commentService.saveComment(entityId, commentType, saveDto);

      if (newCommentId) {
        dispatch(saveCommentSuccess(newCommentId, isCancel));
      }

      if (saveCallback) {
        saveCallback(newCommentId);
      }
      dispatch(actions.reset(FormNames.CommentForm));
      dispatch(clearUsers());
    } catch (e) {
      dispatch(saveCommentFailure(e.message));
    }
  };
}

export function saveCommentStart(): SaveCommentStartAction {
  return {
    type: CommentActionType.SaveCommentStart,
    message: 'Saving Comment Start'
  };
}

export function saveCommentSuccess(commentId: number, isCancel?: boolean): SaveCommentSuccessAction {
  if (!isCancel) {
    toast.success(
      <ToastComponent type={ToastType.Success} message={`Comment Saved Successfully`} />,
      ToastOptions.Success
    );
  }

  return {
    type: CommentActionType.SaveCommentSuccess,
    message: 'Comment Saved Successfully',
    commentId: commentId
  };
}

export function saveCommentFailure(errorMessage: string): SaveCommentFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={errorMessage} />, ToastOptions.Error);

  return {
    type: CommentActionType.SaveCommentFailure,
    errorMessage: errorMessage
  };
}

export function addUser(user: ProfileViewModel): AddUserToCommentAction {
  return {
    type: CommentActionType.AddUserToComment,
    user: user
  };
}

export function removeUser(userEmail: string): RemoveUserFromCommentAction {
  return {
    type: CommentActionType.RemoveUserFromComment,
    userEmail: userEmail
  };
}

export function clearUsers(): ClearUsersAction {
  return {
    type: CommentActionType.ClearUsers
  };
}



// WEBPACK FOOTER //
// ./src/comments/comments.actions.tsx